import { useEffect, useRef, useState } from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlanningMeasurementRequest, PlanningRequestWafer } from "@/types/planning";
import { deepEqual } from "@/lib/utils";
import { WaferRequestRow } from "./WaferRequestRow";

type ExpandedMeasurementRequestRowProps = {
  onWaferSelectionChange: (selection: Set<string>) => void;
  request: PlanningMeasurementRequest;
  selectedWafers: Set<string>;
  showCheckboxes: boolean;
  status: "MEASURED" | "SCHEDULED";
  waferRequestIds: number[];
};
export function ExpandedMeasurementRequestRow({
  onWaferSelectionChange,
  request,
  selectedWafers,
  showCheckboxes,
  status,
  waferRequestIds,
}: ExpandedMeasurementRequestRowProps) {
  const previousWafersRef = useRef<PlanningRequestWafer[]>([]);
  const [waferRequests, setWaferRequests] = useState<PlanningRequestWafer[]>([]);

  const wafersById = new Map(waferRequests.map((wafer) => [wafer._id, wafer]));

  const orderedWafers = waferRequestIds
    .map((id) => wafersById.get(id))
    .filter((wafer): wafer is PlanningRequestWafer => wafer !== undefined);

  useEffect(() => {
    if (
      request.waferRequests.length > 0 &&
      !deepEqual(request.waferRequests, previousWafersRef.current)
    ) {
      setWaferRequests(request.waferRequests);
      previousWafersRef.current = request.waferRequests;
    }
  }, [request.name, request.waferRequests]);

  return (
    <TableRow key={`${request.id}-expanded`}>
      <TableCell
        colSpan={status === "MEASURED" ? (showCheckboxes ? 10 : 9) : showCheckboxes ? 12 : 11}
        className="p-0"
      >
        <div className="bg-blue-50 py-3 *:overflow-y-hidden">
          <Table>
            <TableHeader>
              <TableRow className="h-8">
                {status !== "MEASURED" && <TableHead className="w-12 h-8" />}
                {status !== "MEASURED" && <TableHead className="w-12 h-8" />}
                {showCheckboxes && <TableHead className="w-12 h-8" />}
                <TableHead className="h-8">Wafer ID</TableHead>
                <TableHead className="h-8">System</TableHead>
                {status !== "MEASURED" && <TableHead className="h-8">Order</TableHead>}
                <TableHead className="h-8">Status</TableHead>
                <TableHead className="h-8">Test Label</TableHead>
                <TableHead className="h-8">Dies</TableHead>
                <TableHead className="h-8">Time per die</TableHead>
                <TableHead className="h-8">Total time</TableHead>
                {status === "MEASURED" && <TableHead className="h-8">Start time</TableHead>}
                {status === "MEASURED" && <TableHead className="h-8">Completed time</TableHead>}
                {status === "MEASURED" && <TableHead className="h-8">Actual duration</TableHead>}
                {status !== "MEASURED" && <TableHead className="h-8">Est. finish time</TableHead>}
                <TableHead className="h-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext
                items={orderedWafers.map((wafer) => `WaferRequest-${request.id}-${wafer._id}`)}
                strategy={verticalListSortingStrategy}
              >
                {orderedWafers.map((wafer) => {
                  const waferKey = `${request.id}-${wafer._id}`;

                  const isRelevantWafer =
                    status === "SCHEDULED"
                      ? wafer.status === "SCHEDULED"
                      : status === "MEASURED"
                        ? wafer.status !== "SCHEDULED"
                        : true;

                  if (!isRelevantWafer) return null;

                  return (
                    <WaferRequestRow
                      key={waferKey}
                      onWaferSelectionChange={onWaferSelectionChange}
                      selectedWafers={selectedWafers}
                      showCheckboxes={showCheckboxes}
                      requestId={request.id}
                      status={status}
                      testLabel={request.testLabel}
                      wafer={wafer}
                      waferKey={waferKey}
                    />
                  );
                })}
              </SortableContext>
            </TableBody>
          </Table>
        </div>
      </TableCell>
    </TableRow>
  );
}
